import React, { useEffect, useState, useMemo } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/Components/ui/tabs";
import Iterator from "src/Components/utils/Iterator";
import Visibility from "src/Components/utils/Visibility";
import { OrderStatus } from "src/enums/orderStatus";
import { indexStore } from "src/store/indexStore";
import OrderRow from "../OrderRow";
import OrderRowSkeleton from "../OrderRowSkeleton";
import { SelectBox } from "src/Components/selectbox/Selectbox";
import { DatePickerWithRange } from "src/Components/DatePicker/DatePickerRange";
import { useTranslation } from "react-i18next";
import { Button } from "src/Components/ui/button";
import { cn } from "src/lib/utils";
import { 
  X, 
  Filter, 
  CreditCard, 
  Package, 
  Calendar, 
  Sparkles, 
  ShoppingCart,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  Truck
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/Components/ui/card";
import { Badge } from "src/Components/ui/badge";

const OrderTabs = ({ statuses, branchId, fetchOrdersByStatusFunction }) => {
  const {
    orders,
    ordersLoading,
    setOrder,
    setStatus,
    status,
    lang,
    unreadOrdersCount,
  } = indexStore();
  const { t } = useTranslation();

  // State للفلاتر
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    if (status !== OrderStatus.pending) {
      fetchOrdersByStatusFunction(status, branchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, branchId]);

  // مسح البادج لما المستخدم يفتح تبويب pending
  useEffect(() => {
    if (status === OrderStatus.pending) {
      indexStore.setState({ hasNewPending: false });
    }
  }, [status]);

  // فلترة الطلبات بناءً على الفلاتر المحددة
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // فلتر طريقة الدفع
      if (paymentMethodFilter && paymentMethodFilter !== "all" && order.payment_method !== paymentMethodFilter) {
        return false;
      }

      // فلتر نوع الطلب
      if (orderTypeFilter && orderTypeFilter !== "all" && order.type !== orderTypeFilter) {
        return false;
      }

      // فلتر التاريخ
      if (dateRange?.from) {
        const orderDate = new Date(order.created_at);
        const fromDate = new Date(dateRange.from);
        fromDate.setHours(0, 0, 0, 0);

        if (dateRange.to) {
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          if (orderDate < fromDate || orderDate > toDate) {
            return false;
          }
        } else {
          // إذا تم اختيار تاريخ واحد فقط
          const fromDateEnd = new Date(fromDate);
          fromDateEnd.setHours(23, 59, 59, 999);
          if (orderDate < fromDate || orderDate > fromDateEnd) {
            return false;
          }
        }
      }

      return true;
    });
  }, [orders, paymentMethodFilter, orderTypeFilter, dateRange]);

  // مسح جميع الفلاتر
  const clearFilters = () => {
    setPaymentMethodFilter("all");
    setOrderTypeFilter("all");
    setDateRange(null);
  };

  // التحقق من وجود فلاتر نشطة
  const hasActiveFilters = (paymentMethodFilter && paymentMethodFilter !== "all") || (orderTypeFilter && orderTypeFilter !== "all") || dateRange;

  const getStatusConfig = (statusKey) => {
    switch (statusKey) {
      case OrderStatus.pending:
        return {
          icon: Clock,
          color: "text-yellow-600 dark:text-yellow-400",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
          borderColor: "border-yellow-200 dark:border-yellow-800",
          activeBg: "bg-yellow-100 dark:bg-yellow-900/30",
          badgeColor: "bg-yellow-500",
        };
      case OrderStatus.processing:
        return {
          icon: Loader2,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          borderColor: "border-blue-200 dark:border-blue-800",
          activeBg: "bg-blue-100 dark:bg-blue-900/30",
          badgeColor: "bg-blue-500",
        };
      case OrderStatus.delivarying:
        return {
          icon: Truck,
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-900/20",
          borderColor: "border-purple-200 dark:border-purple-800",
          activeBg: "bg-purple-100 dark:bg-purple-900/30",
          badgeColor: "bg-purple-500",
        };
      case OrderStatus.completed:
        return {
          icon: CheckCircle2,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          borderColor: "border-green-200 dark:border-green-800",
          activeBg: "bg-green-100 dark:bg-green-900/30",
          badgeColor: "bg-green-500",
        };
      case OrderStatus.canseled:
        return {
          icon: XCircle,
          color: "text-red-600 dark:text-red-400",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          borderColor: "border-red-200 dark:border-red-800",
          activeBg: "bg-red-100 dark:bg-red-900/30",
          badgeColor: "bg-red-500",
        };
      default:
        return {
          icon: Package,
          color: "text-gray-600 dark:text-gray-400",
          bgColor: "bg-gray-50 dark:bg-gray-900/20",
          borderColor: "border-gray-200 dark:border-gray-800",
          activeBg: "bg-gray-100 dark:bg-gray-900/30",
          badgeColor: "bg-gray-500",
        };
    }
  };

  return (
    <Tabs
      defaultValue={status}
      onValueChange={setStatus}
      className="w-full"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <TabsList className="grid w-full grid-cols-5 bg-muted/50 p-1 rounded-lg border gap-0.5">
        {statuses.map((tab) => {
          const config = getStatusConfig(tab.key);
          const Icon = config.icon;
          const isActive = status === tab.key;
          const hasNotification = tab.key === OrderStatus.pending && 
            indexStore().hasNewPending && 
            unreadOrdersCount > 0;

          return (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              className={cn(
                "relative flex items-center justify-center gap-2 px-3 py-2.5",
                "rounded-md transition-all duration-200",
                "font-medium text-sm min-h-[2.5rem]",
                "hover:scale-[1.01] active:scale-[0.99]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                // Always have border to maintain consistent size, but make it transparent when inactive
                "border-2",
                isActive 
                  ? `${config.activeBg} ${config.color} ${config.borderColor} shadow-sm` 
                  : `${config.bgColor} ${config.color} border-transparent hover:${config.activeBg} hover:${config.borderColor}`
              )}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${isActive && tab.key === OrderStatus.processing ? 'animate-spin' : isActive ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline whitespace-nowrap">{tab.title}</span>
              <span className="sm:hidden whitespace-nowrap">{tab.title.split(' ')[0]}</span>
              {hasNotification && (
                <Badge 
                  className={`absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center ${config.badgeColor} text-white text-xs font-bold animate-pulse rounded-full`}
                >
                  {unreadOrdersCount > 9 ? '9+' : unreadOrdersCount}
                </Badge>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {statuses.map((tab) => (
        <TabsContent key={tab.key} value={tab.key} className="mt-6">
          {/* Filters Card */}
          <Card className="border-2 mb-6">
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Filter className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{t("gloabl.filter_orders") || "Filter Orders"}</CardTitle>
                    <CardDescription>{t("gloabl.filter_orders_desc") || "Filter orders by payment method, type, or date"}</CardDescription>
                  </div>
                </div>
                {hasActiveFilters && (
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200">
                    {t("gloabl.active_filters")}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Payment Method Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {t("gloabl.payment_method")}
                  </label>
                  <SelectBox
                    placeholder={t("gloabl.all")}
                    value={paymentMethodFilter}
                    onValueChange={setPaymentMethodFilter}
                    data={[
                      { label: t("gloabl.all"), value: "all" },
                      {
                        label: t("paymentMethod.cash"),
                        value: "cash",
                      },
                      {
                        label: t("paymentMethod.credit"),
                        value: "credit",
                      },
                    ]}
                    valueKey="value"
                    label="label"
                    width="w-full"
                  />
                </div>

                {/* Order Type Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {t("gloabl.type")}
                  </label>
                  <SelectBox
                    placeholder={t("gloabl.all")}
                    value={orderTypeFilter}
                    onValueChange={setOrderTypeFilter}
                    data={[
                      { label: t("gloabl.all"), value: "all" },
                      {
                        label: t("orderType.delivary"),
                        value: "delivary",
                      },
                      {
                        label: t("orderType.take_away"),
                        value: "take_away",
                      },
                      {
                        label: t("orderType.dein_in"),
                        value: "dine_in",
                      },
                    ]}
                    valueKey="value"
                    label="label"
                    width="w-full"
                  />
                </div>

                {/* Date Range Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t("gloabl.date_range") || t("gloabl.created_at")}
                  </label>
                  <DatePickerWithRange
                    date={dateRange}
                    setDate={setDateRange}
                    className="w-full"
                  />
                </div>

                {/* Clear Filters Button */}
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="default"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="w-full gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t("gloabl.clear")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Orders List */}
          <div className="space-y-4">
            <Visibility visible={!ordersLoading && filteredOrders.length > 0}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">
                    {t("gloabl.orders_found") || "Orders"} ({filteredOrders.length})
                  </h3>
                </div>
              </div>
            </Visibility>

            <Visibility visible={ordersLoading}>
              <div className="space-y-4">
                <OrderRowSkeleton />
                <OrderRowSkeleton />
                <OrderRowSkeleton />
              </div>
            </Visibility>

            <Visibility visible={!ordersLoading && filteredOrders.length > 0}>
              <div className="space-y-4">
                <Iterator
                  data={filteredOrders}
                  render={(order) => (
                    <OrderRow key={order.id} order={order} setOrder={setOrder} />
                  )}
                />
              </div>
            </Visibility>

            <Visibility visible={!ordersLoading && filteredOrders.length === 0}>
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ShoppingCart className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-xl font-semibold mb-2">
                    {hasActiveFilters
                      ? t("gloabl.no_orders_match_filters") || "No orders match the selected filters"
                      : t("gloabl.no_orders_found") || "No orders found"}
                  </p>
                  <p className="text-sm text-muted-foreground text-center">
                    {hasActiveFilters
                      ? t("gloabl.try_different_filters") || "Try adjusting your filters"
                      : t("gloabl.orders_will_appear") || "Orders will appear here"}
                  </p>
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      className="mt-4 gap-2"
                    >
                      <X className="w-4 h-4" />
                      {t("gloabl.clear_filters") || t("gloabl.clear")}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Visibility>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default OrderTabs;
